import { Controller } from "@hotwired/stimulus"
import Front from "@frontapp/plugin-sdk"

const SIGN_IN_POLL_INTERVAL = 3000
const SIGN_IN_POLL_TIMEOUT = 120000

export default class extends Controller {
  static targets = ["frame"]
  static values = { conversationUrl: String, signInUrl: String, noConversation: String, contextError: String }

  connect() {
    this.onPopupMessage = this.onPopupMessage.bind(this)
    window.addEventListener("message", this.onPopupMessage)

    this.subscription = Front.contextUpdates.subscribe({
      next: (frontContext) => {
        this.frontContext = frontContext
        this.conversationId = frontContext?.conversation?.id
        this.loadConversation()
      },
      error: (error) => {
        console.error("Kactus sidebar: Front context subscription failed", error)
        this.showMessage(this.contextErrorValue)
      }
    })
  }

  disconnect() {
    window.removeEventListener("message", this.onPopupMessage)
    this.subscription?.unsubscribe()
    this.stopPollingForSignIn()
  }

  signIn(event) {
    event.preventDefault()

    // Front opens the window itself: outside the iframe sandbox, where an opaque
    // origin would mean no cookies and no CSRF token, and inside its own cookie
    // store, which the desktop app does not share with the system browser.
    if (this.frontContext?.openUrlInPopup) {
      this.frontContext.openUrlInPopup(this.signInUrlValue, { width: 520, height: 640 })
    } else {
      window.open(this.signInUrlValue, "kactus-front-app-sign-in", "width=520,height=640")
    }

    this.pollForSignIn()
  }

  loadConversation() {
    if (!this.conversationId) {
      console.warn("Kactus sidebar: no conversation in Front context", this.frontContext?.type)
      this.showMessage(this.noConversationValue)
      return
    }

    // Never reload() blindly: signing out navigates the frame, so Turbo leaves
    // the sign-out URL as its src, and that URL only answers DELETE.
    const conversationUrl = this.conversationUrlValue.replace("__ID__", encodeURIComponent(this.conversationId))

    if (this.frameTarget.getAttribute("src") === conversationUrl) {
      this.frameTarget.reload()
    } else {
      this.frameTarget.setAttribute("src", conversationUrl)
    }
  }

  // The sign-in window belongs to Front, so it can neither be watched nor reach
  // back through window.opener: the sidebar checks for itself whether the
  // sign-in prompt is gone.
  pollForSignIn() {
    this.stopPollingForSignIn()
    const giveUpAt = Date.now() + SIGN_IN_POLL_TIMEOUT

    this.signInPoller = setInterval(() => {
      if (!this.signInPending || Date.now() > giveUpAt) {
        this.stopPollingForSignIn()
        return
      }

      this.loadConversation()
    }, SIGN_IN_POLL_INTERVAL)
  }

  stopPollingForSignIn() {
    if (this.signInPoller) clearInterval(this.signInPoller)
    this.signInPoller = null
  }

  get signInPending() {
    return Boolean(this.element.querySelector("[data-front-app-sign-in]"))
  }

  onPopupMessage(event) {
    if (event.origin !== window.location.origin) return
    if (event.data?.type !== "front-app-authenticated") return

    this.stopPollingForSignIn()
    this.loadConversation()
  }

  showMessage(message) {
    this.frameTarget.textContent = message
  }
}
